<script setup>
const buckets = [
  {
    id: 'now',
    label: 'NOW',
    subtitle: 'Available today',
    accent: 'green',
    items: [
      { name: 'Solo plan', detail: 'free, always', lead: true },
      { name: 'Apple Silicon' },
      {
        name: 'Run secure sandboxed workloads',
        children: [
          'Devcontainers (VSCode extension)',
          'Docker containers',
          'Docker compose',
          'WebAssembly (WASM)',
          'Unikernels',
        ],
      },
      { name: 'Basic policy enforcement' },
    ],
  },
  {
    id: 'next',
    label: 'NEXT',
    subtitle: 'In flight',
    accent: 'blue',
    items: [
      { name: 'Team plan', lead: true },
      { name: 'Team policy enforcement' },
      {
        name: 'Hosted infrastructure',
        children: ['Build cluster', 'OCI registry'],
      },
      { name: 'Single Sign On (SSO)' },
      { name: 'Shared team volumes' },
    ],
  },
  {
    id: 'later',
    label: 'LATER',
    subtitle: 'Planned — coming soon',
    accent: 'gray',
    items: [
      { name: 'Enterprise plan', lead: true },
      {
        name: 'Completely self-hosted',
        children: ['SLSA3 build server', 'OCI registry', 'Admin portal', 'Policy server'],
      },
      { name: 'Linux OS support — priority follows demand' },
      { name: 'Windows (WSL2) — priority follows demand' },
      { name: 'SBOM & vulnerability scanning' },
    ],
  },
]
</script>

<template>
  <main class="bg-white pt-24 pb-20 px-4">
    <div class="max-w-7xl mx-auto">
      <header class="mb-8">
        <h1 class="text-5xl md:text-6xl font-bold tracking-tight" style="color: var(--color-krate-dark-900);">Roadmap</h1>
        <p class="mt-4 text-base md:text-lg max-w-2xl" style="color: var(--color-krate-dark-600); line-height: 1.55;">
          What we've shipped, what's in flight, and what's planned for the future.
        </p>
      </header>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
        <section
          v-for="bucket in buckets"
          :key="bucket.id"
          :class="['bucket', `bucket-${bucket.accent}`]"
        >
          <span :class="['bucket-pill', `bucket-pill-${bucket.accent}`]">{{ bucket.label }}</span>
          <div class="bucket-subtitle">{{ bucket.subtitle }}</div>
          <ul class="bucket-list">
            <li
              v-for="(item, idx) in bucket.items"
              :key="item.name"
              :class="['bucket-item', { 'bucket-item-last': idx === bucket.items.length - 1 }]"
            >
              <div :class="{ 'bucket-item-lead': item.lead }">
                {{ item.name }}<template v-if="item.detail"> <span class="bucket-item-detail">— {{ item.detail }}</span></template>
              </div>
              <ul v-if="item.children" class="bucket-children">
                <li v-for="child in item.children" :key="child">• {{ child }}</li>
              </ul>
            </li>
          </ul>
        </section>
      </div>

      <p class="mt-8 max-w-2xl text-base md:text-lg" style="color: var(--color-krate-dark-600); line-height: 1.55;">
        Plans evolve. Follow our
        <a href="https://github.com/krate-dot-sh" style="color: var(--color-krate-blue-600);">GitHub org</a>
        for the latest roadmap updates.
      </p>
    </div>
  </main>
</template>

<style scoped>
.bucket {
  border-radius: 10px;
  padding: 1.25rem;
}
.bucket-green { background: #f0fdf4; border: 1px solid #bbf7d0; }
.bucket-blue  { background: var(--color-krate-blue-50); border: 1px solid var(--color-krate-blue-200); }
.bucket-gray  { background: var(--color-krate-dark-50); border: 1px solid var(--color-krate-dark-200); }

.bucket-pill {
  display: inline-block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 0.18rem 0.7rem;
  border-radius: 9999px;
  margin-bottom: 0.4rem;
}
.bucket-pill-green { color: #15803d; background: #bbf7d0; }
.bucket-pill-blue  { color: var(--color-krate-blue-700); background: var(--color-krate-blue-200); }
.bucket-pill-gray  { color: var(--color-krate-dark-600); background: var(--color-krate-dark-200); }

.bucket-subtitle {
  font-size: 0.78rem;
  color: var(--color-krate-dark-500);
  margin-bottom: 0.9rem;
}

.bucket-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.9rem;
  color: var(--color-krate-dark-900);
  line-height: 1.5;
}

.bucket-item {
  padding: 0.32rem 0;
  border-bottom: 1px solid rgba(100, 116, 139, 0.1);
}
.bucket-green .bucket-item { border-bottom-color: rgba(22, 163, 74, 0.1); }
.bucket-blue  .bucket-item { border-bottom-color: rgba(37, 99, 235, 0.1); }
.bucket-item-last { border-bottom: none; }

.bucket-item-lead { font-weight: 700; }
.bucket-item-detail { font-weight: 400; color: var(--color-krate-dark-500); font-size: 0.85em; }

.bucket-children {
  list-style: none;
  padding: 0.3rem 0 0 1rem;
  margin: 0;
  font-size: 0.85rem;
  color: var(--color-krate-dark-600);
}
.bucket-children li { padding: 0.18rem 0; }
</style>
